<ng-container *ngIf="!loading; else reviewsLoading">

  <div *ngIf="loadError" class="app-tint-danger p-3 mb-3 border-round">
    {{ loadError }}
    <button pButton type="button" label="Try again" class="p-button-text p-button-sm ml-2" (click)="load()"></button>
  </div>

  <!-- Import: Google Takeout today, the v4 API once Google approves access. -->
  <div class="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
    <div class="text-sm text-color-secondary review-intro">
      Nothing appears on picaluna.com until it is approved here, then published from the Website page.
      Re-importing is safe: a review edited on Google comes back here for re-checking, and one deleted on Google is taken off the site.
    </div>
    <input #fileInput type="file" accept=".zip,.json" multiple hidden (change)="onFilesChosen($event)" />
    <button
      pButton pRipple type="button"
      [label]="importing ? 'Importing…' : 'Import Google Takeout'"
      [icon]="importing ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"
      [disabled]="importing"
      class="p-button-rounded flex-shrink-0"
      (click)="chooseFile()"
    ></button>
  </div>

  <div *ngIf="lastImport" class="app-surface-tint p-3 mb-3 border-round text-sm flex align-items-start justify-content-between">
    <div>
      <strong>Imported {{ lastImport.received }} reviews from {{ lastImport.locations }} listings.</strong>
      {{ lastImport.added }} new · {{ lastImport.changed }} changed on Google
      <span *ngIf="lastImport.requeued">({{ lastImport.requeued }} already approved, now back for re-checking)</span>
      · {{ lastImport.unchanged }} unchanged
      <span *ngIf="lastImport.removed"> · {{ lastImport.removed }} no longer on Google (taken off the site)</span>
      <span *ngIf="lastImport.restored"> · {{ lastImport.restored }} back on Google</span>
    </div>
    <button pButton type="button" icon="pi pi-times" class="p-button-text p-button-rounded p-button-plain p-button-sm" pTooltip="Dismiss" tooltipPosition="top" (click)="lastImport = null"></button>
  </div>

  <!-- Status filter -->
  <p-selectButton
    class="block mb-3 review-status"
    [options]="tabOptions" [ngModel]="tab" (ngModelChange)="setTab($event)"
    optionLabel="label" optionValue="value"
  ></p-selectButton>

  <!-- Filters -->
  <div class="flex flex-wrap align-items-center gap-2 mb-3">
    <span class="p-input-icon-left review-search">
      <i class="pi pi-search"></i>
      <input pInputText type="text" class="w-full" placeholder="Search text, reviewer or listing"
        [(ngModel)]="search" (ngModelChange)="onFilterChange()" />
    </span>
    <p-multiSelect
      [options]="ratingOptions" [(ngModel)]="ratings" (ngModelChange)="onFilterChange()"
      optionLabel="label" optionValue="value" defaultLabel="Any rating" [showHeader]="false"
      selectedItemsLabel="{0} ratings" appendTo="body" styleClass="review-filter" panelStyleClass="review-panel"
    ></p-multiSelect>
    <p-dropdown
      [options]="locationOptions" [(ngModel)]="locationId" (ngModelChange)="onFilterChange()"
      optionLabel="label" optionValue="value" placeholder="All listings" [showClear]="true"
      [filter]="true" filterBy="label" appendTo="body" styleClass="review-filter-wide" panelStyleClass="review-panel"
    ></p-dropdown>
    <p-dropdown
      [options]="sortOptions" [(ngModel)]="sort" (ngModelChange)="onFilterChange()"
      optionLabel="label" optionValue="value" appendTo="body" styleClass="review-filter" panelStyleClass="review-panel"
    ></p-dropdown>
    <div class="flex align-items-center">
      <p-checkbox [(ngModel)]="textOnly" (ngModelChange)="onFilterChange()" [binary]="true" inputId="review-text-only"></p-checkbox>
      <label for="review-text-only" class="ml-2 text-sm">With text only</label>
    </div>
    <div class="flex align-items-center">
      <p-checkbox [(ngModel)]="unassignedOnly" (ngModelChange)="onFilterChange()" [binary]="true" inputId="review-unassigned"></p-checkbox>
      <label for="review-unassigned" class="ml-2 text-sm">No arranger yet</label>
    </div>
    <button *ngIf="hasFilters" pButton type="button" label="Clear filters" class="p-button-text p-button-sm" (click)="clearFilters()"></button>
  </div>

  <!-- Bulk bar -->
  <div class="review-bulk-bar app-surface-tint flex flex-wrap align-items-center gap-2 p-2 mb-2 border-round">
    <span class="text-sm font-medium mr-2 white-space-nowrap">
      {{ visible.length }} showing<span *ngIf="selected.size"> · {{ selected.size }} selected</span>
    </span>
    <button *ngIf="!allVisibleSelected" pButton type="button" class="p-button-text p-button-sm"
      [label]="'Select all ' + visible.length" [disabled]="!visible.length" (click)="selectAllVisible()"></button>
    <button *ngIf="selected.size" pButton type="button" label="Clear" class="p-button-text p-button-sm" (click)="clearSelection()"></button>
    <span class="flex-grow-1"></span>
    <button pButton pRipple type="button" icon="pi pi-check" class="p-button-rounded p-button-success p-button-sm"
      [label]="'Approve' + (selected.size ? ' ' + selected.size : '')" [disabled]="!selected.size || busy"
      (click)="moderateSelected('approve')"></button>
    <button pButton pRipple type="button" icon="pi pi-replay" class="p-button-rounded p-button-secondary p-button-sm"
      label="Back to review" [disabled]="!selected.size || busy" (click)="moderateSelected('unapprove')"></button>
    <button pButton pRipple type="button" icon="pi pi-eye-slash" class="p-button-rounded p-button-danger p-button-sm"
      label="Suppress" [disabled]="!selected.size || busy" (click)="moderateSelected('suppress')"></button>
  </div>

  <div class="text-xs text-color-secondary mb-2">
    Keyboard: <kbd>↑</kbd>/<kbd>↓</kbd> move · <kbd>a</kbd> approve · <kbd>s</kbd> suppress · <kbd>p</kbd> back to review · <kbd>x</kbd> select
  </div>

  <div #queue tabindex="0" class="review-queue" aria-label="Google reviews">
  <p-table
    [value]="visible"
    [paginator]="visible.length > pageSize"
    [rows]="pageSize"
    [(first)]="first"
    dataKey="id"
    [rowTrackBy]="trackById"
    styleClass="p-datatable-sm"
    responsiveLayout="scroll"
  >
    <ng-template pTemplate="header">
      <tr>
        <th style="width: 3rem">
          <p-checkbox [binary]="true" [ngModel]="allVisibleSelected" [disabled]="!visible.length"
            (onChange)="$event.checked ? selectAllVisible() : clearSelection()" title="Select all showing"></p-checkbox>
        </th>
        <th style="min-width: 16rem">Review</th>
        <th style="width: 11rem">Arrangers</th>
        <th style="width: 6rem"></th>
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-r>
      <tr [class.review-row-focused]="r.id === focusedId" (click)="focusRow(r)">
        <td>
          <p-checkbox [binary]="true" [ngModel]="isSelected(r)" (onChange)="toggleSelected(r)"></p-checkbox>
        </td>
        <td>
          <div class="flex flex-wrap align-items-center gap-2 mb-1">
            <p-rating [ngModel]="r.starRating || 0" [readonly]="true" [cancel]="false" [stars]="5"
              [attr.aria-label]="(r.starRating || 0) + ' out of 5 stars'" styleClass="review-rating"></p-rating>
            <strong>{{ r.reviewerName || 'A Google user' }}</strong>
            <span class="text-xs text-color-secondary">{{ r.createTime | date: 'd MMM y' }} · {{ r.locationName || r.locationId }}</span>
            <p-chip *ngIf="r.requeueReason === 'edited'" label="Edited on Google" styleClass="app-chip-action review-chip"
              pTooltip="This review was approved, then changed on Google. Check the new wording." tooltipPosition="top"></p-chip>
            <p-chip *ngIf="r.removedFromGoogleAt" label="Removed from Google" styleClass="app-chip-neutral review-chip"
              pTooltip="The latest import of this listing no longer had it. It will not appear on the site." tooltipPosition="top"></p-chip>
            <p-chip *ngIf="tab === 'all' || tab === 'removed'"
              [label]="r.status === 'approved' ? 'Approved' : r.status === 'suppressed' ? 'Suppressed' : 'To review'"
              [styleClass]="'review-chip ' + (r.status === 'approved' ? 'app-chip-ok' : r.status === 'suppressed' ? 'app-chip-danger' : 'app-chip-neutral')"></p-chip>
          </div>
          <div *ngIf="r.comment; else starOnly" class="review-comment" [class.review-comment-clamped]="!expanded.has(r.id)">{{ r.comment }}</div>
          <ng-template #starOnly><div class="text-color-secondary text-sm font-italic">Star rating only — no text. Shows in the rating total, never as a quote.</div></ng-template>
          <button *ngIf="r.comment && r.comment.length > 280" pButton type="button" class="p-button-text p-button-sm p-0 mt-1"
            [label]="expanded.has(r.id) ? 'Show less' : 'Show all'" (click)="toggleExpanded(r); $event.stopPropagation()"></button>
          <div *ngIf="r.replyComment" class="review-reply text-sm mt-2">
            <span class="font-medium">Our reply on Google:</span> {{ r.replyComment }}
          </div>
        </td>
        <td>
          <p-multiSelect
            [options]="arrangerOptions" [ngModel]="r.arrangerUserIds" (ngModelChange)="onArrangersChange(r, $event)"
            optionLabel="label" optionValue="value" [filter]="true" [maxSelectedLabels]="1"
            selectedItemsLabel="{0} arrangers" defaultLabel="General pool" appendTo="body"
            styleClass="w-full review-arrangers" panelStyleClass="review-panel"
          ></p-multiSelect>
          <div *ngIf="mentions(r).length" class="text-xs mt-1">
            <span class="text-color-secondary">Mentions:</span>
            <button *ngFor="let m of mentions(r)" pButton type="button" class="p-button-link p-button-sm p-0 ml-1"
              [label]="m.label" [title]="'Attribute this review to ' + m.label + '? Check the review is really about them.'"
              (click)="addArranger(r, m.value); $event.stopPropagation()"></button>
          </div>
        </td>
        <td class="white-space-nowrap text-right">
          <button *ngIf="r.status !== 'approved'" pButton pRipple type="button" icon="pi pi-check"
            class="p-button-rounded p-button-success mr-1" pTooltip="Approve for the website (a)" tooltipPosition="top"
            [disabled]="busy" (click)="moderateOne(r, 'approve'); $event.stopPropagation()"></button>
          <button *ngIf="r.status !== 'pending'" pButton pRipple type="button" icon="pi pi-replay"
            class="p-button-rounded p-button-secondary mr-1" pTooltip="Back to review (p)" tooltipPosition="top"
            [disabled]="busy" (click)="moderateOne(r, 'unapprove'); $event.stopPropagation()"></button>
          <button *ngIf="r.status !== 'suppressed'" pButton pRipple type="button" icon="pi pi-eye-slash"
            class="p-button-rounded p-button-danger" pTooltip="Suppress — never show this review (s)" tooltipPosition="top"
            [disabled]="busy" (click)="moderateOne(r, 'suppress'); $event.stopPropagation()"></button>
        </td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="4" class="text-center text-color-secondary py-5">
          <ng-container *ngIf="!counts.all">No reviews yet. Use "Import Google Takeout" to bring them in.</ng-container>
          <ng-container *ngIf="counts.all && hasFilters">No reviews match these filters.</ng-container>
          <ng-container *ngIf="counts.all && !hasFilters && tab === 'pending'">Nothing left to review.</ng-container>
          <ng-container *ngIf="counts.all && !hasFilters && tab !== 'pending'">No reviews here.</ng-container>
        </td>
      </tr>
    </ng-template>
  </p-table>
  </div>

</ng-container>

<ng-template #reviewsLoading>
  <div class="flex flex-column align-items-center justify-content-center p-6 text-color-secondary">
    <i class="pi pi-spin pi-spinner text-3xl mb-3" aria-hidden="true"></i>
    <span>Loading…</span>
  </div>
</ng-template>
